import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { IFormElementOptionProp } from 'models/form';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { FormSelect } from 'components/molecules/FormSelect';
import { Box, Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { useAlert } from 'models/alertContext';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { IAuthenticationSetupItem, ItrustedDeviceOptions } from 'models/settings';
import { useLang } from 'models/langContext';
import { ActionTypes } from 'state/actions/alert';
import { useLicenceContext } from 'models/licenceContext';
import TableFormWrapper from 'components/atoms/FormWrapper';
import useAuthenticationSetupFormSchema from './schemaValidation';
import { useAuthenticationSetup } from './hooks';
import { StyledValueFormHeader } from '../styles';

const numberOfInvalidLoginAttempts = {
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
};

const timePeriodOfAccountLockout = (t: (key: string) => string) => ({
  '15': `15 ${t('authenticationSetup.minutes.label')}`,
  '30': `30 ${t('authenticationSetup.minutes.label')}`,
  '60': `1 ${t('authenticationSetup.hour.label')}`,
  '1440': `1 ${t('authenticationSetup.day.label')}`,
  '999999999': t('authenticationSetup.perm.ban.label'),
});
const twoFactorAuthenticationOptions = (t: (key: string) => string) => ({
  true: `${t('authenticationSetup.enable.label')}`,
  false: `${t('authenticationSetup.disable.label')}`,
});

export enum EAuthenticationMethod {
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  AUTHENTICATOR = 'AUTHENTICATOR',
  NONE = 'NONE',
}

export interface IFormValues {
  twoFactorAuthentication: boolean;
  authenticationMethod: string;
  timePeriods: string[];
  numberOfInvalidLoginAttempts: string;
  timePeriodOfAccountLockout: string;
}

const initialFormValues: IFormValues = {
  twoFactorAuthentication: false,
  authenticationMethod: EAuthenticationMethod.NONE,
  timePeriods: [],
  numberOfInvalidLoginAttempts: '',
  timePeriodOfAccountLockout: '',
};

const AuthenticationSetupPage = () => {
  const { t } = useTranslations();
  const { samePageAlert, clearAlert, setAlert } = useAlert();
  const navigate = useNavigate();
  const { selectedLanguage } = useLang();
  const {
    handleUpdate,
    getAuthenticationSetupData,
    authenticationSetup,
    getTrustedDeviceOptionsData,
    trustedDeviceOptions,
  } = useAuthenticationSetup();

  const [initialValues, setInitialValues] = useState<IFormValues>(initialFormValues);
  const { isOnlineLicence } = useLicenceContext();
  const { schema } = useAuthenticationSetupFormSchema();
  const [isSubmitLoading, setSubmitLoading] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<IFormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(schema) as unknown as Resolver<IFormValues>,
  });

  const authenticationMethodOptions = [
    {
      label: t('authenticationMethod.sms.label'),
      value: EAuthenticationMethod.SMS,
    },
    {
      label: t('authenticationMethod.email.label'),
      value: EAuthenticationMethod.EMAIL,
    },
    {
      label: t('authenticationMethod.authenticator.label'),
      value: EAuthenticationMethod.AUTHENTICATOR,
    },
  ];

  const authenticationMethodOptionsOffline = [
    {
      label: t('authenticationMethod.authenticator.label'),
      value: EAuthenticationMethod.AUTHENTICATOR,
    },
  ];

  const authenticationMethodTooltipText = [
    {
      text: t('authenticationSetup.method.sms.tooltip.text'),
      value: EAuthenticationMethod.SMS,
    },
    {
      text: t('authenticationSetup.method.email.tooltip.text'),
      value: EAuthenticationMethod.EMAIL,
    },
    {
      text: t('authenticationSetup.method.authenticator.tooltip.text'),
      value: EAuthenticationMethod.AUTHENTICATOR,
    },
  ];

  const watchTwoFactorAuthentication = watch('twoFactorAuthentication');

  useEffect(() => {
    if (watchTwoFactorAuthentication.toString() === 'false') {
      setValue('authenticationMethod', EAuthenticationMethod.NONE);
    }
  }, [watchTwoFactorAuthentication]);

  const prepareSelectOptions = (list: IAuthenticationSetupItem): IFormElementOptionProp[] =>
    Object.keys(list).map((item) => ({
      value: item,
      label: list[item],
    }));

  useEffect(() => {
    reset(initialValues);
  }, [reset, initialValues]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (authenticationSetup) {
      setInitialValues({
        twoFactorAuthentication: authenticationSetup.two_fa_enabled,
        authenticationMethod: authenticationSetup.authentication_method,
        timePeriods: authenticationSetup.timePeriods,
        numberOfInvalidLoginAttempts: authenticationSetup.attempts_to_block,
        timePeriodOfAccountLockout: authenticationSetup?.minutes_in_ban,
      });
    }
  }, [authenticationSetup]);

  useEffect(() => {
    (async function getDataInit() {
      await getAuthenticationSetupData();
      await getTrustedDeviceOptionsData();
    })();
  }, [selectedLanguage]);

  const areArraysEqual = (arr1: string[] | undefined, arr2: string[]) => {
    if (arr1 === undefined) return false;
    if (
      arr1
        .filter((it) => it !== undefined)
        .filter((it) => it.toString().toLowerCase() === 'true').length !==
      arr2
        .filter((it) => it !== undefined)
        .filter((it) => it.toString().toLowerCase() === 'true').length
    ) {
      return false;
    }
    const sortedArr1 = arr1.slice().sort();
    const sortedArr2 = arr2.slice().sort();
    return sortedArr1.every((value, index) => value === sortedArr2[index]);
  };

  const formChanged = (
    formInitialValues?: IFormValues | null,
    formCurrentValues?: IFormValues | null,
  ) =>
    formInitialValues?.twoFactorAuthentication !==
      formCurrentValues?.twoFactorAuthentication ||
    formInitialValues?.authenticationMethod !== formCurrentValues?.authenticationMethod ||
    formInitialValues?.numberOfInvalidLoginAttempts !==
      formCurrentValues?.numberOfInvalidLoginAttempts ||
    formInitialValues?.timePeriodOfAccountLockout !==
      formCurrentValues?.timePeriodOfAccountLockout ||
    !areArraysEqual(
      formInitialValues?.timePeriods || [],
      formCurrentValues?.timePeriods || [],
    );

  const onSubmitForm = async (data: IFormValues) => {
    if (!formChanged(initialValues, data)) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
      return;
    }
    setSubmitLoading(true);
    const dataTimePeriods: ItrustedDeviceOptions[] = [];
    // eslint-disable-next-line array-callback-return
    trustedDeviceOptions?.map((item) => {
      if (item.daysToExpire && data.timePeriods[item.daysToExpire] !== undefined) {
        dataTimePeriods.push({
          uniqueId: item.uniqueId,
          enabled: !!data.timePeriods[item.daysToExpire],
        });
      } else {
        dataTimePeriods.push({ uniqueId: item.uniqueId, enabled: item.enabled });
      }
    });

    const dataToUpdate: any = {
      ...data,
      timePeriods: dataTimePeriods,
    };
    await handleUpdate(dataToUpdate);
    setSubmitLoading(false);
    setInitialValues(data);
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          t('authenticationSetup.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('authenticationSetup.header')}</StyledPageTitle>

      <TableFormWrapper>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Grid container>
            <Grid size={{ xs: 12 }} sx={{ rowGap: 1 }}>
              <Grid size={{ xs: 12 }} container columnSpacing={2} sx={{ rowGap: 3 }}>
                <Grid size={{ xs: 12 }}>
                  <StyledValueFormHeader>
                    {t('authenticationSetup.twoFactorAuthentication.header')}
                  </StyledValueFormHeader>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ mb: 1 }}>
                    <FormRadioGroup
                      name="twoFactorAuthentication"
                      label={t('authenticationSetup.twoFactorAuthentication.label')}
                      options={prepareSelectOptions(twoFactorAuthenticationOptions(t))}
                      control={control}
                      errors={errors}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">
                      {t('authenticationSetup.tomePeriods.label')}
                    </Typography>
                    <Grid
                      container
                      sx={{ justifyContent: 'space-between', maxWidth: '550px' }}
                    >
                      {trustedDeviceOptions?.map((option) => (
                        <Grid size={{ xs: 6, sm: 'auto' }}>
                          <FormCheckbox
                            key={option.uniqueId}
                            name={`timePeriods[${option.daysToExpire}]`}
                            label={`${option.label}`}
                            control={control}
                            errors={errors}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }} container columnSpacing={2} sx={{ rowGap: 3 }}>
                <Grid size={{ xs: 12 }}>
                  <StyledValueFormHeader>
                    {t('authenticationSetup.authenticationMethod.header')}
                  </StyledValueFormHeader>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Grid container size={{ xs: 12 }}>
                    <Box sx={{ mb: 1 }}>
                      <FormRadioGroup
                        name="authenticationMethod"
                        label={t('authenticationSetup.authenticationMethod.label')}
                        options={
                          isOnlineLicence()
                            ? authenticationMethodOptions
                            : authenticationMethodOptionsOffline
                        }
                        control={control}
                        errors={errors}
                        disabled={
                          watchTwoFactorAuthentication &&
                          watchTwoFactorAuthentication.toString() === 'false'
                        }
                        helperTooltipText={authenticationMethodTooltipText}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }} container columnSpacing={2} sx={{ rowGap: 3 }}>
                <Grid size={{ xs: 12 }}>
                  <StyledValueFormHeader>
                    {t('authenticationSetup.failedAuthenticationSettings.header')}
                  </StyledValueFormHeader>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ mb: 1 }}>
                    <FormSelect
                      name="numberOfInvalidLoginAttempts"
                      label={`${t('authenticationSetup.numberOfInvalidLoginAttempts.label')}?`}
                      setValue={setValue}
                      options={prepareSelectOptions(numberOfInvalidLoginAttempts)}
                      canBeEmpty={false}
                      control={control}
                      errors={errors}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ mb: 1 }}>
                    <FormSelect
                      name="timePeriodOfAccountLockout"
                      label={`${t('authenticationSetup.timePeriodOfAccountLockout.label')}?`}
                      setValue={setValue}
                      options={prepareSelectOptions(timePeriodOfAccountLockout(t))}
                      canBeEmpty={false}
                      control={control}
                      errors={errors}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 1, mt: 1 }}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.save.button')}
                id="save-button"
                isLoading={isSubmitLoading}
                variant={EButtonVariants.contained}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Button
                fullWidth
                label={t('general.cancel.button')}
                id="cancel-button"
                variant={EButtonVariants.outlined}
                onClick={() => navigate(ERouteLinks.Administration)}
              />
            </Grid>
          </Grid>
        </form>
      </TableFormWrapper>

      {samePageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
    </>
  );
};

export default AuthenticationSetupPage;
